<template>
    <div class="simulationOverrides">
        <p class="override-title">{{ getText('simulation_override_title') }}</p>
        <el-form class="override-form" :model="form">
            <div v-for="(value, index) in availableSolutions" :key="index">
                <div v-if="index === 0" class="first-column">
                    <div class="empty-column"></div>
                    <div class="column-name">
                        {{ availableSolutionsNames[value] }}
                        <el-tooltip effect="light" popper-class="simulationCreationTooltip" :visible-arrow="false"
                            :content="getText(`simulation_override_reset_initial_values_${value}`)" placement="top">
                            <el-button icon="el-icon-refresh-left" size="mini"
                                @click="resetToInitalvalues(value)"></el-button>
                        </el-tooltip>
                    </div>
                </div>
                <div v-else class="column-name">
                    {{ availableSolutionsNames[value] }}
                    <el-tooltip effect="light" popper-class="simulationCreationTooltip" :visible-arrow="false"
                        :content="getText(`simulation_override_reset_initial_values_${value}`)" placement="top">
                        <el-button icon="el-icon-refresh-left" size="mini"
                            @click="resetToInitalvalues(value)"></el-button>
                    </el-tooltip>
                </div>
                <el-form-item :label="index === 0 ? getText('simulation_overrides_solution_cost') : ''">
                    <custom-input :ref="`${value}motorCost`" v-model="form[value].motorCost"
                        :disabled="value === 'current'" :defaultValue="previousValues[value].motorCost"></custom-input>
                </el-form-item>
                <el-form-item :label="index === 0 ? getText('simulation_overrides_variator_cost') : ''">
                    <custom-input :ref="`${value}variatorCost`" v-model="form[value].variatorCost"
                        :defaultValue="previousValues[value].variatorCost"></custom-input>
                </el-form-item>
                <el-form-item :label="index === 0 ? getText('simulation_overrides_setup_cost') : ''">
                    <custom-input :ref="`${value}setupCost`" v-model="form[value].setupCost"
                        :defaultValue="previousValues[value].setupCost"></custom-input>
                </el-form-item>
            </div>
        </el-form>
        <el-button type="text" class="restart greenButton" @click="relaunchSimulation">{{ getText('simulation_restart')
        }}</el-button>
        <el-button type="text" class="restart greenButton" @click="resetToInitalvalues(null)">{{
            getText('simulation_override_reset_initial_values') }}</el-button>
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
import customInput from '../partials/customInput.vue';

export default {

    components: {
        customInput,
    },

    props: {
        simulation: {
            type: Object,
            required: true
        }
    },

    data: function () {
        return {
            availableSolutions: ['current', 'ie2', 'ie3', 'ie4', 'msap'],
            availableSolutionsNames: {
                'ie2': 'IE2',
                'ie3': 'IE3',
                'ie4': 'IE4',
                'msap': 'MSAP',
                'current': 'Actuel'
            },
            availableSpeeds: [750, 1000, 1500, 3000],
            form: {
                nominal_power: 0,
                nominal_speed: 0,
                eff_variator: 0,
                ie2: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                ie3: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                ie4: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                msap: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                current: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 1,
                },
            },
            previousValues: {
                nominal_power: 0,
                nominal_speed: 0,
                eff_variator: 0,
                ie2: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                ie3: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                ie4: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                msap: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 0,
                },
                current: {
                    motorCost: 0,
                    variatorCost: 0,
                    setupCost: 0,
                    motorEfficiency: 1,
                },
            },
        };
    },

    computed: {
        ...mapGetters([
            'getText',
        ]),
        getPower() {
            return this.simulation.motor.nominal_power;
        },
        getSpeed() {
            return this.availableSpeeds.find(speed => {
                return speed >= this.simulation.motor.nominal_speed;
            });
        },
        initialCostValues() {
            let defaultTestedSolutions = this.simulation.default_tested_solutions;
            let initialCosts = {}

            this.availableSolutions.forEach(solution => {
                const closestCostsPower = this.getClosestPower(solution, this.getPower, 'costs', defaultTestedSolutions); 
                const closestEfficienciesPower = this.getClosestPower(solution, this.getPower, 'efficiencies', defaultTestedSolutions);

                initialCosts[solution] = {
                    motorCost: solution === 'current' ? 0 : defaultTestedSolutions[solution].costs[closestCostsPower][this.getSpeed],
                    variatorCost: defaultTestedSolutions[solution].costs[closestCostsPower].variator,
                    setupCost: defaultTestedSolutions[solution].costs[closestCostsPower].setup,
                    motorEfficiency: solution === 'current' ? 1 : defaultTestedSolutions[solution].efficiencies[closestEfficienciesPower][this.getSpeed],
                }
            });

            return initialCosts;
        }

    },
    mounted() {
        this.availableSolutionsNames.current = this.getText('simulation_override_current_label');
        this.setForm();
    },
    methods: {
        getClosestPower(solution, power, type, data=this.simulation.custom_tested_solutions){
            if(type === 'efficiencies' && solution === 'current'){
                // efficiency for current dont require closest power since we always set it at 1
                return power;
            }
            let availabePower = Object.keys(data[solution][type]);
            let closestPower = availabePower.reduce((prev, curr) => {
                return (Math.abs(curr - power) < Math.abs(prev - power) ? curr : prev);
            });
            return closestPower;
        },
        setForm() {
            this.form.nominal_power = this.getPower;
            this.form.nominal_speed = this.getSpeed;
            this.form.eff_variator = this.simulation.default_tested_solutions.eff_variator

            let customSolutions = { ...this.simulation.custom_tested_solutions };

            this.availableSolutions.forEach(solution => {
                const closestCostsPower = this.getClosestPower(solution, this.getPower, 'costs'); 
                const closestEfficienciesPower = this.getClosestPower(solution, this.getPower, 'efficiencies');
                this.form[solution].variatorCost = customSolutions[solution].costs[closestCostsPower].variator
                this.form[solution].setupCost = customSolutions[solution].costs[closestCostsPower].setup
                if (solution !== 'current') {
                    this.form[solution].motorCost = customSolutions[solution].costs[closestCostsPower][this.getSpeed];
                    this.form[solution].motorEfficiency = customSolutions[solution].efficiencies[closestEfficienciesPower][this.getSpeed];
                } else {
                    this.form[solution].motorCost = 0;
                    this.form[solution].motorEfficiency = 1;
                }
            })

            this.previousValues = JSON.parse(JSON.stringify(this.form))
        },
        relaunchSimulation() {
            let customCurrentMotor = { ...this.simulation.custom_current_motor };
            let customTestedSolutions = { ...this.simulation.default_tested_solutions };

            this.availableSolutions.forEach(solution => {
                const closestCostsPower = this.getClosestPower(solution, this.getPower, 'costs'); 
                const closestEfficienciesPower = this.getClosestPower(solution, this.getPower, 'efficiencies');
                if (customTestedSolutions[solution].costs[closestCostsPower] === undefined) {
                    customTestedSolutions[solution].costs[closestCostsPower] = {};
                }
                customTestedSolutions[solution].costs[closestCostsPower][this.getSpeed] = parseFloat(this.form[solution].motorCost);
                customTestedSolutions[solution].costs[closestCostsPower].variator = parseFloat(this.form[solution].variatorCost);
                customTestedSolutions[solution].costs[closestCostsPower].setup = parseFloat(this.form[solution].setupCost);
                if (customTestedSolutions[solution].efficiencies[closestEfficienciesPower] === undefined) {
                    customTestedSolutions[solution].efficiencies[closestEfficienciesPower] = {};
                }
                if (solution !== 'current') {
                    customTestedSolutions[solution].efficiencies[closestEfficienciesPower][this.getSpeed] = parseFloat(this.form[solution].motorEfficiency);
                } else {
                    customTestedSolutions[solution].efficiencies[closestEfficienciesPower][this.getSpeed] = 1;
                }
                customTestedSolutions.eff_variator = parseFloat(this.form.eff_variator);
            });

            let item = {
                'custom_current_motor': customCurrentMotor,
                'custom_tested_solutions': customTestedSolutions,
                'status': 'PENDING',
                'notify_status': 'NOT_NEEDED'
            };
            this.$store.axios.patch(this.simulation.url, item).then(() => {
                this.$notify.success({ message: this.getText('simulation_override_success') });
                this.$router.push({ name: 'simulations' });
            }).catch(() => {
                this.$notify.error({ message: this.getText('simulation_override_failed') });
            });
        },
        resetToInitalvalues(solution = null) {
            if (!solution) {
                this.form = JSON.parse(JSON.stringify(this.initialCostValues));
                this.form.nominal_power = this.getPower;
                this.form.nominal_speed = this.getSpeed;
                this.form.eff_variator = this.simulation.default_tested_solutions.eff_variator
                this.availableSolutions.forEach(solution => {
                    this.$refs[`${solution}motorCost`][0].saveValue(this.form[solution].motorCost);
                    this.$refs[`${solution}variatorCost`][0].saveValue(this.form[solution].variatorCost);
                    this.$refs[`${solution}setupCost`][0].saveValue(this.form[solution].setupCost);
                });
            } else {
                this.$set(this.form[solution], 'motorCost', this.initialCostValues[solution].motorCost);
                this.$refs[`${solution}motorCost`][0].saveValue(this.form[solution].motorCost);

                this.$set(this.form[solution], 'variatorCost', this.initialCostValues[solution].variatorCost);
                this.$refs[`${solution}variatorCost`][0].saveValue(this.form[solution].variatorCost);

                this.$set(this.form[solution], 'setupCost', this.initialCostValues[solution].setupCost);
                this.$refs[`${solution}setupCost`][0].saveValue(this.form[solution].setupCost);

                this.$set(this.form[solution], 'motorEfficiency', this.initialCostValues[solution].motorEfficiency);
            }
        }
    },
};
</script>

<style lang="less">
@import "../../less/variables.less";

.simulationOverrides {
    .simulation-motor-select {

        input,
        input:focus,
        input:hover,
        .el-select .el-input.is-focus .el-input__inner {
            border: none;
            border-radius: 0;
            border-bottom: 1px solid @color-1;
        }
    }

    .override-title {
        margin: 30px 0 0 5%;
    }

    .simulation-motor-select {
        margin-left: 2%;
        width: 250px;
    }

    .restart {
        margin-left: 2%;
        margin-top: 20px;
    }

    .override-form {
        display: flex;
        gap: 2rem;
        margin-left: 2%;

        .first-column {
            display: flex;

            .empty-column {
                flex-grow: 1;
            }

            .column-name {
                width: 160px;
            }
        }

        .column-name {
            display: flex;
            align-items: center;
            justify-content: center;
            border-bottom: 1px solid @color-1;
            text-align: center;
            height: 30px;
            margin: 1rem 0;

            .el-button {
                color: @color-1;
                border-color: @color-1;
                margin-left: 2rem;
                margin-bottom: 0.5rem;

                &:hover {
                    background-color: @color-1-hover;
                }
            }
        }

        .el-form-item {
            display: flex;

            &__label {
                flex-grow: 1;
                margin: 0;
                width: auto;
                margin-right: 20px;
                // height: 40px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                transition: all 0.4s;
                font-weight: 400;
                text-align: left;
            }

            .el-input {
                max-width: 160px;
            }
        }
    }
}

.simulationCreationTooltip {
    width: auto;
    border: none !important;
    background: @color-1 !important;
    font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/page/simulationOverrides.vue